import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "gigma_wizard_state_v1";

const DEFAULT_HOURS = {
  Monday: { open: "09:00", close: "17:00", closed: false },
  Tuesday: { open: "09:00", close: "17:00", closed: false },
  Wednesday: { open: "09:00", close: "17:00", closed: false },
  Thursday: { open: "09:00", close: "17:00", closed: false },
  Friday: { open: "09:00", close: "17:00", closed: false },
  Saturday: { open: "10:00", close: "15:00", closed: false },
  Sunday: { open: "", close: "", closed: true },
};

const DEFAULT_PHOTOS = {
  storefront: "pending",
  interior: "pending",
  work_examples: "pending",
  team: "pending",
  logo: "pending",
};

const DEFAULT_STATE = {
  businessInfo: {
    name: "",
    phone: "",
    address: "",
    website: "",
    serviceArea: "",
  },
  businessType: "",
  services: "",
  hours: DEFAULT_HOURS,
  photos: DEFAULT_PHOTOS,
  reviewLink: "",
  payment: { paid: false, sessionId: null },
};

const WizardContext = createContext(null);

export function WizardProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_STATE, ...parsed,
          businessInfo: { ...DEFAULT_STATE.businessInfo, ...(parsed.businessInfo || {}) },
          hours: { ...DEFAULT_HOURS, ...(parsed.hours || {}) },
          photos: { ...DEFAULT_PHOTOS, ...(parsed.photos || {}) },
          payment: { ...DEFAULT_STATE.payment, ...(parsed.payment || {}) },
        };
      }
    } catch (e) {
      // ignore
    }
    return DEFAULT_STATE;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const updateBusinessInfo = useCallback((patch) => {
    setState((s) => ({ ...s, businessInfo: { ...s.businessInfo, ...patch } }));
  }, []);
  const setBusinessType = useCallback((bt) => setState((s) => ({ ...s, businessType: bt })), []);
  const setServices = useCallback((v) => setState((s) => ({ ...s, services: v })), []);
  const updateHours = useCallback((day, patch) =>
    setState((s) => ({ ...s, hours: { ...s.hours, [day]: { ...s.hours[day], ...patch } } })),
  []);
  const updatePhotos = useCallback((cat, val) =>
    setState((s) => ({ ...s, photos: { ...s.photos, [cat]: val } })),
  []);
  const setPayment = useCallback((p) => setState((s) => ({ ...s, payment: { ...s.payment, ...p } })), []);
  const setReviewLink = useCallback((v) => setState((s) => ({ ...s, reviewLink: v })), []);
  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState(DEFAULT_STATE);
  }, []);

  const completeness = computeCompleteness(state);

  const value = {
    state,
    completeness,
    updateBusinessInfo,
    setBusinessType,
    setServices,
    updateHours,
    updatePhotos,
    setPayment,
    setReviewLink,
    reset,
  };

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

export function useWizard() {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error("useWizard must be used inside WizardProvider");
  return ctx;
}

function computeCompleteness(state) {
  const checks = [];
  // Business info: 5 required-ish
  checks.push(!!state.businessInfo.name);
  checks.push(!!state.businessInfo.phone);
  checks.push(!!state.businessInfo.address);
  checks.push(!!state.businessInfo.website);
  checks.push(!!state.businessInfo.serviceArea);
  checks.push(!!state.businessType);
  checks.push(!!state.services && state.services.trim().length > 0);
  // Hours: at least one open day
  const anyOpen = Object.values(state.hours).some((d) => !d.closed && d.open && d.close);
  checks.push(anyOpen);
  // Photos: at least 1 marked ready
  const anyReady = Object.values(state.photos).some((v) => v === "ready");
  checks.push(anyReady);

  const filled = checks.filter(Boolean).length;
  const pct = Math.round((filled / checks.length) * 100);

  const missing = [];
  if (!state.businessInfo.name) missing.push("Business name");
  if (!state.businessInfo.phone) missing.push("Phone number");
  if (!state.businessInfo.address) missing.push("Address");
  if (!state.businessInfo.website) missing.push("Website");
  if (!state.businessInfo.serviceArea) missing.push("Service area");
  if (!state.businessType) missing.push("Business type");
  if (!state.services || !state.services.trim()) missing.push("Services");
  if (!anyOpen) missing.push("Business hours");
  if (!anyReady) missing.push("At least one photo marked ready");

  return { pct, missing };
}

export const BUSINESS_TYPES = [
  { id: "barber", label: "Barber", icon: "scissors", category: "Barber shop" },
  { id: "restaurant", label: "Restaurant", icon: "utensils", category: "Restaurant" },
  { id: "plumber", label: "Plumber", icon: "wrench", category: "Plumber" },
  { id: "electrician", label: "Electrician", icon: "zap", category: "Electrician" },
  { id: "landscaper", label: "Landscaper", icon: "leaf", category: "Landscaper" },
  { id: "auto_repair", label: "Auto repair", icon: "car", category: "Auto repair shop" },
  { id: "other", label: "Other", icon: "store", category: "Local business" },
];

export const PHOTO_CATEGORIES = [
  { id: "storefront", label: "Storefront", desc: "A clean, well-lit exterior so customers recognize you on the street." },
  { id: "interior", label: "Interior", desc: "Show the vibe customers feel walking in." },
  { id: "work_examples", label: "Work examples", desc: "Real before/after or finished work that shows quality." },
  { id: "team", label: "Team photo", desc: "Put friendly faces to your name." },
  { id: "logo", label: "Logo", desc: "Your wordmark or icon — used as the profile avatar." },
];

export const DAYS = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];
